import React, { useState, useEffect, useRef } from "react";
import { Send, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { useFlowContext } from "../hooks/useFlowContext";
import LoginModal from "./LoginModal";
import ExplorationChoiceModal from "./ExplorationChoiceModal";
import defaultWorkflowService from "../services/defaultWorkflowService";
import { explorationExists, loadExploration } from "../utils/explorationUtils";
import { getInputPlaceholders } from "../utils/messagingSelector";

const InputField = ({
  onSubmit,
  isLoading,
  onLoadDefaultWorkflow,
  showLoginModal,
  setShowLoginModal,
}) => {
  const { isAuthenticated, user, login, justLoggedOut, clearLogoutState } =
    useAuth();
  const { startFreshExploration } = useFlowContext();
  const [question, setQuestion] = useState("");
  const [submittedQuestion, setSubmittedQuestion] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [showExplorationChoiceModal, setShowExplorationChoiceModal] =
    useState(false);
  const [pendingQuestion, setPendingQuestion] = useState("");
  const [existingExploration, setExistingExploration] = useState(null);
  const inputRef = useRef(null);
  const typeStateRef = useRef({
    currentIndex: 0,
    placeholderIndex: 0,
    isDeleting: false,
    isPaused: false,
  });

  // Get placeholders from messaging configuration - use state to ensure re-renders
  const [placeholders, setPlaceholders] = useState(() => getInputPlaceholders());

  // Update placeholders when URL changes (version switching)
  useEffect(() => {
    const handleVersionChange = () => {
      const newPlaceholders = getInputPlaceholders();
      setPlaceholders(newPlaceholders);

      // Reset typing state to start fresh with new placeholders
      typeStateRef.current = {
        currentIndex: 0,
        placeholderIndex: 0,
        isDeleting: false,
        isPaused: false,
      };
      setDisplayedPlaceholder("");

      console.log('📝 Placeholders updated:', newPlaceholders);
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', handleVersionChange);

    // Check for version changes periodically (optimized with 500ms interval)
    const intervalId = setInterval(() => {
      const currentPlaceholders = getInputPlaceholders();
      // Early bailout if lengths don't match (faster than JSON.stringify)
      if (currentPlaceholders.length !== placeholders.length) {
        handleVersionChange();
        return;
      }
      // Only do deep comparison if lengths match
      if (JSON.stringify(currentPlaceholders) !== JSON.stringify(placeholders)) {
        handleVersionChange();
      }
    }, 500); // Reduced frequency from 100ms to 500ms

    return () => {
      window.removeEventListener('popstate', handleVersionChange);
      clearInterval(intervalId);
    };
  }, [placeholders]);

  const suggestionLinks = justLoggedOut
    ? [
        "Who is Jonah?",
        "Who is Moses?",
        "Story of David and Goliath",
        "Who is Gideon?",
      ]
    : [
        "Who is Jonah?",
        "Who is Moses?",
        "Story of David and Goliath",
        "Who is Gideon?",
      ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (question.trim() && !isLoading) {
      const trimmedQuestion = question.trim();

      try {
        // Check if an exploration already exists for this question
        const exists = await explorationExists(trimmedQuestion, user?.id);
        if (exists) {
          // Load the existing exploration data
          const exploration = await loadExploration(trimmedQuestion, user?.id);
          if (exploration) {
            setPendingQuestion(trimmedQuestion);
            setExistingExploration(exploration);
            setShowExplorationChoiceModal(true);
            return;
          }
        }
      } catch (error) {
        console.error("Failed to check for existing exploration:", error);
        // Continue with normal flow if there's an error
      }

      // No existing exploration, proceed normally
      setSubmittedQuestion(trimmedQuestion);
      setShowLoading(true);
      onSubmit(trimmedQuestion);
      setQuestion("");
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (justLoggedOut && suggestion === "You just logged out. Log in") {
      clearLogoutState();
      setShowLoginModal(true);
      return;
    }

    if (!showLoading) {
      // Check if user is logged out and suggestion has a default workflow
      if (!isAuthenticated && defaultWorkflowService.hasWorkflow(suggestion)) {
        const defaultWorkflow =
          defaultWorkflowService.loadWorkflowForSuggestion(suggestion);
        if (defaultWorkflow && onLoadDefaultWorkflow) {
          onLoadDefaultWorkflow(defaultWorkflow);
          return;
        }
      }

      try {
        // Check if an exploration already exists for this suggestion
        const exists = await explorationExists(suggestion, user?.id);
        if (exists) {
          const exploration = await loadExploration(suggestion, user?.id);
          if (exploration) {
            setPendingQuestion(suggestion);
            setExistingExploration(exploration);
            setShowExplorationChoiceModal(true);
            return;
          }
        }
      } catch (error) {
        console.error("Failed to check for existing exploration:", error);
        // Continue with normal flow if there's an error
      }

      // Regular suggestion handling - no existing exploration
      setQuestion(suggestion);
      setSubmittedQuestion(suggestion);
      setShowLoading(true);
      onSubmit(suggestion);
      setTimeout(() => setQuestion(""), 100);
    }
  };

  // Handle exploration choice modal actions
  const handleContinueExploration = async () => {
    if (existingExploration && onLoadDefaultWorkflow) {
      // Check if this is a Firestore workflow (has _firestoreNodes) or IndexedDB workflow (has nodes array)
      const isFirestoreWorkflow =
        existingExploration._firestoreNodes !== undefined;

      if (isFirestoreWorkflow) {
        // Firestore workflow - need to convert from Firestore format
        try {
          console.log(
            "📥 Loading Firestore exploration:",
            existingExploration.name,
            existingExploration.id
          );
          const { loadWorkflowFromFirestore } = await import(
            "../services/firestoreService.js"
          );
          const workflow = await loadWorkflowFromFirestore(
            user.id,
            existingExploration.id
          );
          if (workflow) {
            onLoadDefaultWorkflow(workflow);
          } else {
            console.error("Failed to load Firestore workflow: Not found");
          }
        } catch (error) {
          console.error("Failed to load Firestore workflow:", error);
        }
      } else {
        // IndexedDB workflow - need to load full data from storage
        try {
          console.log(
            "📥 Loading IndexedDB exploration:",
            existingExploration.name
          );
          const storageService = await import("../services/storageService.js");
          const workflow = await storageService.default.loadWorkflow(
            existingExploration.name
          );
          onLoadDefaultWorkflow(workflow);
        } catch (error) {
          console.error("Failed to load IndexedDB workflow:", error);
          // Fallback to direct loading
          onLoadDefaultWorkflow(existingExploration);
        }
      }
    }
    setShowExplorationChoiceModal(false);
    setPendingQuestion("");
    setExistingExploration(null);
  };

  const handleStartFreshExploration = () => {
    if (pendingQuestion) {
      // Flag that we want a unique title for this exploration
      startFreshExploration();

      setSubmittedQuestion(pendingQuestion);
      setShowLoading(true);
      onSubmit(pendingQuestion);
      setQuestion("");
    }
    setShowExplorationChoiceModal(false);
    setPendingQuestion("");
    setExistingExploration(null);
  };

  const handleCloseExplorationModal = () => {
    setShowExplorationChoiceModal(false);
    setPendingQuestion("");
    setExistingExploration(null);
  };

  const handleInputFocus = () => {
    if (!isAuthenticated) {
      if (justLoggedOut) {
        clearLogoutState();
      }
      setShowLoginModal(true);
    } else {
      setIsFocused(true);
    }
  };

  const handleLogin = async (provider) => {
    try {
      const result = await login(provider);
      if (result.success) {
        setShowLoginModal(false);
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
            setIsFocused(true);
          }
        }, 100);
      }
    } catch (error) {
      console.error(`${provider} login error:`, error);
    }
  };

  // Close login modal when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [isAuthenticated, showLoginModal, setShowLoginModal]);

  // Effect to handle loading state and ensure minimum display time
  useEffect(() => {
    if (isLoading) {
      setShowLoading(true);
    } else if (showLoading) {
      // Keep loading visible for at least 3 seconds for better UX
      const timer = setTimeout(() => {
        setShowLoading(false);
        setSubmittedQuestion("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isLoading, showLoading]);

  // Auto-focus input when component mounts - disabled
  // useEffect(() => {
  //   if (inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, []);

  // Typing effect for placeholder
  useEffect(() => {
    if (!showLoading && !question && !isFocused) {
      const typeInterval = setInterval(
        () => {
          const state = typeStateRef.current;
          const currentPlaceholder = placeholders[state.placeholderIndex];

          if (state.isPaused) {
            return;
          }

          if (!state.isDeleting) {
            // Typing forward
            if (state.currentIndex < currentPlaceholder.length) {
              state.currentIndex++;
              setDisplayedPlaceholder(
                currentPlaceholder.slice(0, state.currentIndex)
              );
            } else {
              // Pause at end, then start deleting
              state.isPaused = true;
              setTimeout(() => {
                state.isPaused = false;
                state.isDeleting = true;
              }, 2000);
            }
          } else {
            // Deleting backward
            if (state.currentIndex > 0) {
              state.currentIndex--;
              setDisplayedPlaceholder(
                currentPlaceholder.slice(0, state.currentIndex)
              );
            } else {
              // Move to next placeholder
              state.placeholderIndex =
                (state.placeholderIndex + 1) % placeholders.length;
              state.isDeleting = false;
              state.currentIndex = 0;
            }
          }
        },
        typeStateRef.current.isDeleting ? 50 : 100
      );

      return () => clearInterval(typeInterval);
    } else {
      // Reset typing state when user starts typing or loading
      typeStateRef.current = {
        currentIndex: 0,
        placeholderIndex: 0,
        isDeleting: false,
        isPaused: false,
      };
      setDisplayedPlaceholder("");
    }
  }, [showLoading, question, isFocused, placeholders]);

  return (
    <div className="mx-auto py-6 px-0" style={{ width: "90%" }}>
      <form onSubmit={handleSubmit} className="relative">
        <motion.div
          className="relative"
          animate={{
            scale: isLoading ? 0.98 : 1,
          }}
          transition={{ duration: 0.2 }}>
          <div
            className="relative"
            style={{ borderRadius: "9999px" }}>
            {/* Glowing border effect on hover/focus */}
            <style>{`
              @keyframes border-glow {
                0%, 100% {
                  box-shadow: 0 0 0 0 rgba(59, 130, 246, 0),
                              0 0 20px 0 rgba(59, 130, 246, 0);
                }
                50% {
                  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4),
                              0 0 35px 3px rgba(59, 130, 246, 0.3);
                }
              }
              .input-glow:hover,
              .input-glow:focus {
                animation: border-glow 2s ease-in-out infinite !important;
              }
            `}</style>
            <input
              ref={inputRef}
              type="text"
              value={showLoading ? submittedQuestion : question}
              onChange={(e) => !showLoading && setQuestion(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={() => setIsFocused(false)}
              placeholder=""
              style={{
                backgroundColor: '#ffffff',
                willChange: 'box-shadow'
              }}
              className={`input-glow w-full pl-6 pr-14 py-5 text-xl border-2 rounded-full focus:outline-none transition-all ${
                showLoading
                  ? "border-primary text-gray-500 cursor-wait"
                  : "border-gray-300 focus:border-primary hover:border-gray-400"
              } ${
                question === "" && !showLoading && !isFocused
                  ? "caret-transparent"
                  : ""
              }`}
              disabled={showLoading}
            />
            {question === "" && !showLoading && !isFocused && (
              <div className="absolute left-6 right-16 top-1/2 transform -translate-y-1/2 pointer-events-none text-xl text-gray-400 flex items-center overflow-hidden whitespace-nowrap">
                <span className="truncate">{displayedPlaceholder}</span>
                <span
                  className="inline-block w-0.5 h-5 bg-gray-400 ml-0.5 flex-shrink-0"
                  style={{
                    animation: "blink 1s infinite",
                  }}></span>
                <style>{`
                  @keyframes blink {
                    0%,
                    50% {
                      opacity: 1;
                    }
                    51%,
                    100% {
                      opacity: 0;
                    }
                  }
                `}</style>
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={!question.trim() || showLoading}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-3 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            {showLoading ? (
              <Loader2 size={22} className="animate-spin" />
            ) : (
              <Send size={22} />
            )}
          </button>
        </motion.div>

        <AnimatePresence>
          {showLoading && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-3 text-center overflow-hidden">
              <motion.div
                className="inline-flex items-center bg-white/80 px-4 py-2 rounded-full shadow-sm"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{
                  duration: 0.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}>
                <motion.div
                  className="flex space-x-1 mr-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}>
                  <motion.div
                    className="w-2 h-2 bg-primary rounded-full"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-primary rounded-full"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-primary rounded-full"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                  />
                </motion.div>
                <span className="text-sm text-gray-600 font-medium">
                  Searching the scriptures
                </span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      {/* Suggestion Links */}
      {!showLoading && (
        <div className="mt-6 text-center relative z-50">
          {!isAuthenticated && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.4 }}
              className="text-sm text-gray-400 mb-3 tracking-wide uppercase">
              Try These Examples
            </motion.p>
          )}
          <div className="flex flex-wrap justify-center gap-3 relative z-50">
            {suggestionLinks.map((suggestion, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  delay: 1.2 + index * 0.1,
                  duration: 0.4,
                  type: "spring",
                  stiffness: 200,
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleSuggestionClick(suggestion)}
                style={{ willChange: "transform" }}
                className={`group relative text-blue-600 hover:text-blue-800 transition-colors text-lg font-medium ${
                  !isAuthenticated ? "pl-5" : ""
                }`}>
                {!isAuthenticated && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-xs">
                    →
                  </span>
                )}
                <span className="border-b-2 border-transparent group-hover:border-blue-600 transition-all">
                  {suggestion}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
      />

      <ExplorationChoiceModal
        isOpen={showExplorationChoiceModal}
        onClose={handleCloseExplorationModal}
        onContinue={handleContinueExploration}
        onStartFresh={handleStartFreshExploration}
        question={pendingQuestion}
        explorationData={existingExploration}
      />
    </div>
  );
};

export default InputField;
